'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { SmartLink } from '../../fragments/smart-link/SmartLink';
import NavigationMainLogo from './NavigationMainLogo';
import NavLevel2Menu from './NavLevel2Menu';
import Cta from '../../fragments/cta/Cta';
import Button from '../../fragments/button/Button';
import IconUI from '../../fragments/icon/IconUI';

// Desktop navigation. Rendered (alongside NavigationMobile) by NavigationMain,
// and shown/hidden purely via the `.NavigationMain` breakpoint styles. The
// search overlay is owned by NavigationMain; `onOpenSearch` triggers it.
export default function NavigationDesktop({ block, onOpenSearch }) {
  const [openMenuId, setOpenMenuId] = useState(null);
  // Measured height of the open Level 2 panel's `-inner`, published as
  // `--nav-panel-height` so both the panel and the white nav background
  // (`.NavigationDesktop-nav::before`) size themselves off the actual content
  // instead of a fixed `--max-nav-height`. The last value is kept on close so
  // the collapse transition starts from the right height.
  const [panelHeight, setPanelHeight] = useState(0);
  const navRef = useRef(null);
  const router = useRouter();

  // Stable identity — NavLevel2Menu has this in a layout-effect dependency list.
  const handlePanelHeight = useCallback((height) => {
    setPanelHeight(height);
  }, []);

  useEffect(() => {
    const handleRouteChange = () => setOpenMenuId(null);
    router.events.on('routeChangeStart', handleRouteChange);
    // Same-page clicks cancel navigation (no routeChangeStart), so SmartLink
    // emits `samePageNav` — close the dropdown on that too.
    window.addEventListener('samePageNav', handleRouteChange);
    return () => {
      router.events.off('routeChangeStart', handleRouteChange);
      window.removeEventListener('samePageNav', handleRouteChange);
    };
  }, [router.events]);

  // useEffect(() => {
  //   const pageWrapper = document.querySelector('.pageWrapper');

  //   if (!pageWrapper) return;

  //   let ticking = false;

  //   const handleScroll = () => {
  //     if (!ticking) {
  //       requestAnimationFrame(() => {
  //         if (window.scrollY > 100) {
  //           pageWrapper.classList.add('scrolled');
  //         } else {
  //           pageWrapper.classList.remove('scrolled');
  //         }
  //         setOpenMenuId(null);
  //         ticking = false;
  //       });
  //       ticking = true;
  //     }
  //   };

  //   window.addEventListener('scroll', handleScroll, { passive: true });
  //   return () => {
  //     window.removeEventListener('scroll', handleScroll);
  //     document.body.classList.remove('scrolled');
  //   };
  // }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) {
        setOpenMenuId(null);
      }
    };
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && openMenuId !== null) {
        setOpenMenuId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [openMenuId]);

  if (!block || !block.menuItems) return null;

  const centerLinks = block.menuItems;
  const rightLink = block.applyLink;

  const handleToggle = (id) => {
    setOpenMenuId((prev) => (prev === id ? null : id));
  };

  return (
    <div className="NavigationDesktop">
      <div className="NavigationDesktop-container" aria-label="Main Navigation">
        <nav
          className="NavigationDesktop-nav"
          ref={navRef}
          style={{ '--nav-panel-height': `${panelHeight}px` }}
        >
          <SmartLink
            href={block.site.link}
            className="Logo"
            title={block.site.title}
          >
            <NavigationMainLogo />
          </SmartLink>

          {centerLinks.length > 0 && (
            <ul className="NavLevel1Menu">
              {centerLinks.map((item) => {
                const hasChildren = item.children?.length > 0;
                const isOpen = openMenuId === item.id;
                const menuId = `nav-menu-${item.id}`;
                return (
                  <li
                    key={item.id}
                    className={`NavLevel1Menu-item ${
                      hasChildren ? 'menu-item-has-children' : ''
                    } ${isOpen ? 'hover' : ''} ${item.classes?.join(' ') || ''}`}
                  >
                    {hasChildren ? (
                      <button
                        type="button"
                        className="button button--tertiary"
                        aria-expanded={isOpen}
                        aria-controls={menuId}
                        data-size="regular"
                        onClick={() => handleToggle(item.id)}
                      >
                        <div className="button-inner">
                          <div className="button-text">
                            <span>{item.label}</span>
                          </div>
                          <IconUI icon="chevronDown" />
                        </div>
                      </button>
                    ) : (
                      <Cta
                        cta={{
                          url: item.uri,
                          buttonLabel: item.label,
                          isExternal: false,
                        }}
                        icon="chevronDown"
                        theme="tertiary"
                      />
                    )}
                    {hasChildren && (
                      <NavLevel2Menu
                        id={menuId}
                        children={item.children}
                        title={item.label}
                        uri={item.uri}
                        image={item.featuredImage}
                        excerpt={item.excerpt}
                        isOpen={isOpen}
                        onHeight={handlePanelHeight}
                      />
                    )}
                  </li>
                );
              })}
            </ul>
          )}
          <div className="NavigationDesktop-right">
            <div className="NavigationDesktop-search">
              <Button
                icon="search"
                theme="secondary"
                aria-label="Open search"
                onClick={onOpenSearch}
                size="small"
              />
            </div>
            {rightLink && (
              <Cta
                size="large"
                // icon="arrowNarrowRight"
                cta={{
                  url: rightLink.url,
                  buttonLabel: rightLink.title,
                  // isExternal: !!rightLink.target,
                }}
              />
            )}
          </div>
        </nav>
      </div>
    </div>
  );
}
